"use client";

import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./components/ui/button";
import { OTPVerification } from "./components/OTPVerification";
import { TermsAndConditions } from "./components/TermsAndConditions";
import { DocumentCollection } from "./components/DocumentCollection";
import { DocumentReview } from "./components/DocumentReview";
import { DocumentCorrection } from "./components/DocumentCorrection";
import { AppointmentLetter } from "./components/AppointmentLetter";
import { ProgressDemo } from "./components/ProgressDemo";
import { 
  FileText, 
  Upload, 
  PenTool, 
  ArrowRight, 
  CheckCircle,
  Settings,
  BarChart3
} from "lucide-react";
import openTurfLogo from './assets/OpenTurf-logo.png';

type AppView = "demo" | "otp" | "terms" | "documents" | "review" | "correction" | "appointment" | "progress" | "complete";

export default function App() {
  const [currentView, setCurrentView] = React.useState<AppView>("demo");

  const handleShowTerms = () => {
    setCurrentView("terms");
  };

  const handleBackToDemo = () => {
    setCurrentView("demo");
  };

  const handleBackToOTP = () => {
    setCurrentView("otp");
  };

  const handleVerificationSuccess = () => {
    setCurrentView("documents");
  };

  const handleDocumentCollectionComplete = () => {
    setCurrentView("review");
  };

  const handleDocumentReviewComplete = () => {
    setCurrentView("complete");
  };

  const handleCorrectionComplete = () => {
    setCurrentView("complete");
  };

  const handleAppointmentComplete = () => {
    setCurrentView("complete");
  };

  const handleBackToDocuments = () => {
    setCurrentView("documents");
  };

  // Demo workflow handlers
  const handleStartDocumentUpload = () => {
    setCurrentView("otp");
  };

  const handleStartCorrection = () => {
    setCurrentView("correction");
  };

  const handleStartAppointmentSigning = () => {
    setCurrentView("appointment");
  };

  const handleStartProgressTracking = () => {
    setCurrentView("progress");
  };

  return (
    <>
      {currentView === "demo" && (
        <DemoScreen 
          onShowTerms={handleShowTerms}
          onStartDocumentUpload={handleStartDocumentUpload}
          onStartCorrection={handleStartCorrection}
          onStartAppointmentSigning={handleStartAppointmentSigning}
          onStartProgressTracking={handleStartProgressTracking}
        />
      )}

      {currentView === "otp" && (
        <OTPVerification 
          onShowTerms={handleShowTerms}
          onVerificationSuccess={handleVerificationSuccess}
        />
      )}
      
      {currentView === "terms" && (
        <TermsAndConditions onBack={handleBackToOTP} />
      )}
      
      {currentView === "documents" && (
        <DocumentCollection 
          onComplete={handleDocumentCollectionComplete}
          onShowTerms={handleShowTerms}
        />
      )}

      {currentView === "review" && (
        <DocumentReview 
          onComplete={handleDocumentReviewComplete}
          onShowTerms={handleShowTerms}
          onBack={handleBackToDocuments}
        />
      )}
      
      {currentView === "correction" && (
        <DocumentCorrection
          onComplete={handleCorrectionComplete}
          onShowTerms={handleShowTerms}
          onBack={handleBackToDemo}
        />
      )}

      {currentView === "appointment" && (
        <AppointmentLetter
          onComplete={handleAppointmentComplete}
          onShowTerms={handleShowTerms}
          onBack={handleBackToDemo}
        />
      )}

      {currentView === "progress" && (
        <ProgressDemo
          onBack={handleBackToDemo}
          onShowTerms={handleShowTerms}
        />
      )}
      
      {currentView === "complete" && (
        <CompletionScreen 
          onBackToDemo={handleBackToDemo}
          onShowTerms={handleShowTerms}
        />
      )}
    </>
  );
}

// Demo Screen Component
function DemoScreen({ 
  onShowTerms, 
  onStartDocumentUpload, 
  onStartCorrection, 
  onStartAppointmentSigning,
  onStartProgressTracking
}: { 
  onShowTerms?: () => void;
  onStartDocumentUpload: () => void;
  onStartCorrection: () => void;
  onStartAppointmentSigning: () => void;
  onStartProgressTracking: () => void;
}) {
  const workflows = [
    {
      id: "document-upload",
      title: "Document Upload Workflow",
      description: "Experience the complete onboarding journey from OTP verification to document collection.",
      icon: Upload,
      color: "from-blue-500 to-blue-600",
      steps: ["OTP Verification", "Document Collection", "Review and Submit"],
      onStart: onStartDocumentUpload
    },
    {
      id: "document-correction",
      title: "Document Correction Workflow", 
      description: "See how candidates handle HR feedback and document resubmission requests.",
      icon: FileText,
      color: "from-orange-500 to-orange-600",
      steps: ["Review Corrections", "Upload Fixed Documents", "Resubmit"],
      onStart: onStartCorrection
    },
    {
      id: "appointment-signing",
      title: "Appointment Letter Workflow",
      description: "Sign your appointment letter with digital signature or upload functionality.",
      icon: PenTool,
      color: "from-green-500 to-green-600", 
      steps: ["Review Letter", "Add Signature", "Submit"],
      onStart: onStartAppointmentSigning
    },
    {
      id: "progress-tracking",
      title: "Progress Tracking Workflow",
      description: "Track onboarding progress with interactive stepper and real-time status updates.",
      icon: BarChart3,
      color: "from-purple-500 to-indigo-600",
      steps: ["View Progress", "Interactive Demo", "Different Layouts"],
      onStart: onStartProgressTracking
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex flex-col relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-32 h-32 bg-purple-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-blue-200/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-radial from-purple-100/10 to-transparent rounded-full" />
      </div>

      {/* Fixed Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 p-6 bg-white/50 backdrop-blur-sm border-b border-gray-200/50"
      >
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-8 w-auto"
            />
          </div>
          <div className="flex items-center space-x-2">
            <Settings className="w-5 h-5 text-purple-600" />
            <span className="text-gray-900">Demo Mode</span>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="relative z-10 w-full max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-blue-500 rounded-2xl mx-auto flex items-center justify-center mb-8">
              <Settings className="w-10 h-10 text-white" />
            </div>
            
            <h1 className="text-gray-900 mb-4">
              DayOne Demo Workflows 🚀
            </h1>
            
            <p className="text-gray-600 max-w-3xl mx-auto mb-8">
              Explore our comprehensive candidate onboarding platform. Try each workflow independently 
              to see how DayOne streamlines the entire hiring process from document collection to final signing.
            </p>
          </motion.div>

          {/* Workflow Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {workflows.map((workflow, index) => (
              <motion.div
                key={workflow.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.1 }}
                className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300 group"
              >
                <div className={`w-16 h-16 bg-gradient-to-r ${workflow.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <workflow.icon className="w-8 h-8 text-white" />
                </div>
                
                <h3 className="text-gray-900 mb-4">{workflow.title}</h3>
                
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {workflow.description}
                </p>

                {/* Steps Preview */}
                <div className="mb-6">
                  <h4 className="text-sm text-gray-700 mb-3">Workflow Steps:</h4>
                  <div className="space-y-2">
                    {workflow.steps.map((step, stepIndex) => (
                      <div key={stepIndex} className="flex items-center space-x-3">
                        <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs text-gray-600">
                          {stepIndex + 1}
                        </div>
                        <span className="text-sm text-gray-600">{step}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={workflow.onStart}
                  className={`w-full bg-gradient-to-r ${workflow.color} text-white hover:opacity-90 transition-opacity`}
                >
                  Try This Workflow
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </motion.div>
            ))}
          </div>

          {/* Feature Highlights */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="mt-16 bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-lg border border-white/20"
          >
            <h3 className="text-gray-900 mb-6 text-center">Platform Features</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-6 h-6 text-blue-600" />
                </div>
                <h4 className="text-gray-900 mb-2">AI-Powered Verification</h4>
                <p className="text-sm text-gray-600">Automated document processing with TurfAI</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <FileText className="w-6 h-6 text-green-600" />
                </div>
                <h4 className="text-gray-900 mb-2">Smart Corrections</h4>
                <p className="text-sm text-gray-600">Guided workflow for document fixes</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <PenTool className="w-6 h-6 text-purple-600" />
                </div>
                <h4 className="text-gray-900 mb-2">Digital Signatures</h4>
                <p className="text-sm text-gray-600">Secure electronic signing capabilities</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <BarChart3 className="w-6 h-6 text-indigo-600" />
                </div>
                <h4 className="text-gray-900 mb-2">Progress Tracking</h4>
                <p className="text-sm text-gray-600">Real-time onboarding status updates</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <motion.footer 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="relative z-10 p-6 border-t border-gray-200/50 bg-white/50 backdrop-blur-sm"
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-6 w-auto opacity-60"
            />
            <span className="text-xs text-gray-500">
              © {new Date().getFullYear()} OpenTurf Technologies Pvt. Ltd.
            </span>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span>Powered by TurfAI</span>
            <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
            <button 
              onClick={onShowTerms}
              className="text-purple-600 hover:text-purple-700 hover:underline transition-colors"
            >
              Terms & Conditions
            </button>
          </div>
        </div>
      </motion.footer>
    </div>
  );
}

// Enhanced Completion Screen Component
function CompletionScreen({ 
  onBackToDemo,
  onShowTerms
}: { 
  onBackToDemo: () => void;
  onShowTerms?: () => void;
}) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-purple-50 flex flex-col relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-32 h-32 bg-green-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-purple-200/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-radial from-green-100/10 to-transparent rounded-full" />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="relative z-10 text-center max-w-md mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="w-24 h-24 bg-gradient-to-r from-green-500 to-green-600 rounded-full mx-auto flex items-center justify-center mb-8">
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
            
            <h1 className="text-gray-900 mb-4">
              Workflow Complete! 🎉
            </h1>
            
            <p className="text-gray-600 mb-8">
              You've successfully completed the onboarding workflow. Experience how DayOne streamlines candidate management.
            </p>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/20 mb-8">
              <h3 className="text-gray-900 mb-4">Ready to explore more?</h3>
              <div className="text-left space-y-3 text-sm text-gray-600">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p>Try our document correction workflow</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p>Experience the appointment letter signing process</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p>See the complete candidate journey with progress tracking</p>
                </div>
              </div>
            </div>

            <Button
              onClick={onBackToDemo}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 mb-4"
            >
              Back to Demo Workflows
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>
        </div>
      </div>
      
      {/* Footer */}
      <footer className="relative z-10 p-6 border-t border-gray-200/50 bg-white/50 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-6 w-auto opacity-60"
            />
            <span className="text-xs text-gray-500">
              © {new Date().getFullYear()} OpenTurf Technologies Pvt. Ltd.
            </span>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span>Powered by TurfAI</span>
            <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
            <button 
              onClick={onShowTerms}
              className="text-purple-600 hover:text-purple-700 hover:underline transition-colors"
            >
              Terms & Conditions
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}